import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ICellEditorParams } from '@ag-grid-community/core';

const KEY_BACKSPACE = 'Backspace';
const KEY_F2 = 'F2';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';
const KEY_ARROW_LEFT = 'ArrowLeft';
const KEY_ARROW_RIGHT = 'ArrowRight';

export const AgGridNumericEditor = memo(
  forwardRef((props: ICellEditorParams, ref) => {
    const isLeftOrRight = (event: any) => {
      return [KEY_ARROW_LEFT, KEY_ARROW_RIGHT].indexOf(event.key) > -1;
    };

    const isCharNumeric = (charStr: string) => {
      return !!/[\d.-]/.test(charStr);
    };

    const isDecimal = (charStr: string) => {
      return !!/^-?\d+(\.\d+)?$/.test(charStr);
    };

    const isKeyPressedNumeric = (event: any) => {
      const charStr = event.key;
      return isCharNumeric(charStr);
    };

    const isBackspace = (event: any) => {
      return event.key === KEY_BACKSPACE;
    };

    const isSelectAll = (event: any) => {
      return event.key === 'a' && (event.metaKey || event.ctrlKey);
    };

    const finishedEditingPressed = (event: any) => {
      const key = event.key;
      return key === KEY_ENTER || key === KEY_TAB;
    };

    const onKeyDown = (event: any) => {
      if (isLeftOrRight(event) || isBackspace(event) || isSelectAll(event)) {
        event.stopPropagation();
        return;
      }

      if (!finishedEditingPressed(event) && !isKeyPressedNumeric(event)) {
        if (event.preventDefault) event.preventDefault();
      }

      if (finishedEditingPressed(event)) {
        props.stopEditing();
      }
    };

    const createInitialState = () => {
      let startValue;
      let highlightAllOnFocus = true;

      if (props.eventKey === KEY_BACKSPACE) {
        startValue = null;
      } else if (props.eventKey && isCharNumeric(props.eventKey)) {
        startValue = props.eventKey;
        highlightAllOnFocus = false;
      } else {
        startValue = props.value;
        if (props.eventKey === KEY_F2) {
          highlightAllOnFocus = false;
        }
      }

      return {
        value: startValue,
        highlightAllOnFocus,
      };
    };

    const initialState = createInitialState();
    const [value, setValue] = useState(initialState.value);
    const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(
      initialState.highlightAllOnFocus,
    );
    const refInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
      const eInput = refInput.current!;
      eInput.focus();
      if (highlightAllOnFocus) {
        eInput.select();

        setHighlightAllOnFocus(false);
      } else {
        const length = eInput.value ? eInput.value.length : 0;
        if (length > 0) {
          eInput.setSelectionRange(length, length);
        }
      }
    }, [highlightAllOnFocus]);

    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return value ? Number(value) : null;
        },

        isCancelAfterEnd() {
          return value && !isDecimal(value);
        },
      };
    });

    return (
      <div
        role="presentation"
        className="ag-cell-editor ag-labeled ag-label-align-left ag-text-field ag-input-field"
      >
        <div
          className="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper"
          role="presentation"
        >
          <input
            ref={refInput}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => onKeyDown(event)}
            className="ag-input-field-input ag-text-field-input"
          />
        </div>
      </div>
    );
  }),
);
